<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Turmas</h5>
      <b-button
        v-if="hasPermission('adicionar_turmas')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill" title="Nova Turma"></i>
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.turma"
        class="form-control"
        placeholder="Filtrar por Turma (ID)"
        @input="filtrar"
      />
      <input
        v-model="filters.nivel"
        class="form-control"
        placeholder="Filtrar por Nível"
        @input="filtrar"
      />
      <input
        v-model="filters.instrutor"
        class="form-control"
        placeholder="Filtrar por Instrutor"
        @input="filtrar"
      />
      <select
        v-model="filters.ativo"
        class="form-select"
        style="max-width: 150px;"
        @change="filtrar"
      >
        <option :value="undefined">Filtrar por Status</option>
        <option :value="true">Ativo</option>
        <option :value="false">Inativo</option>
      </select>
      <b-button
        variant="secondary"
        @click="limparFiltros"
        class="d-flex align-items-center"
      >
        <i class="bi bi-funnel" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela customizada -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="sort('id')">
              Turma {{ arrow('id') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('nivel_nome')">
              Nível {{ arrow('nivel_nome') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('instrutor_nome')">
              Instrutor {{ arrow('instrutor_nome') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('ativo')">
              Status {{ arrow('ativo') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="t in turmas" :key="t.id">
          <td>{{ t.id }}</td>
          <td>{{ t.nivel_nome }}</td>
          <td>{{ t.instrutor_nome }}</td>
          <td>{{ t.ativo ? 'Ativo' : 'Inativo' }}</td>
          <td class="text-end">
            <router-link
              v-if="hasPermission('detalhar_turmas')"
              :to="`/cadastros/turmas/${t.id}/detalhar`"
            >
              <i class="bi bi-eye-fill text-primary mx-2" title="Detalhar"></i>
            </router-link>
            <router-link
              v-if="hasPermission('alterar_turmas')"
              :to="`/cadastros/turmas/${t.id}`"
            >
              <i class="bi bi-pencil-fill text-warning mx-2" title="Editar"></i>
            </router-link>
            <i
              v-if="hasPermission('excluir_turmas')"
              class="bi bi-trash-fill text-danger mx-2"
              title="Excluir"
              @click="excluir(t.id)"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <!-- 1. Resumo -->
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>

      <!-- 2. Seletor de itens por página -->
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limitePorPagina"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="onChangeLimit"
        />
      </div>

      <!-- 3. Controle de página -->
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="paginaAtual"
          :per-page="limitePorPagina"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, getCurrentInstance, computed, watch, ref } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import { useSortableTable } from '@/utils/useSortableTable'

const router = useRouter()
const { proxy } = getCurrentInstance()

// composable para turmas
const {
  items: turmas,
  totalItems,
  page: paginaAtual,
  limit: limitePorPagina,
  filters,
  load,
  sort,
  arrow
} = useSortableTable(
  `/cadastros/turmas`,
  { key: 'nivel_nome', order: 'asc' }
)

// opções de limite de página
const pageOptions = ref([10, 15, 20, 25, 50])

// Computeds para o resumo
const startRow = computed(() =>
  (paginaAtual.value - 1) * limitePorPagina.value + 1
)
const endRow = computed(() =>
  Math.min(paginaAtual.value * limitePorPagina.value, totalItems.value)
)

// Watchers para recarregar ao mudar página ou limite
watch(paginaAtual, () => load())
watch(limitePorPagina, () => {
  paginaAtual.value = 1
  load()
})

// Inicialização
onMounted(() => {
  filters.value = { turma: '', nivel: '', instrutor: '', ativo: undefined }
  load()
})

// Handlers de filtro
function filtrar() {
  paginaAtual.value = 1
  load()
}
function limparFiltros() {
  filters.value = { turma: '', nivel: '', instrutor: '', ativo: undefined }
  paginaAtual.value = 1
  load()
}

// Handlers de paginação
function onChangeLimit() {
  paginaAtual.value = 1
  load()
}
function onPageChange() {
  load()
}

// Navegação e ações
function irParaCadastro() {
  router.push('/cadastros/turmas/novo')
}
async function excluir(id) {
  const ok = await proxy.$confirm(
    'Confirma exclusão desta turma?',
    'Confirmação'
  )
  if (!ok) return
  try {
    await axios.delete(`/cadastros/turmas/${id}`)
    load()
    proxy.$toast.success('Turma excluída com sucesso!')
  } catch (e) {
    const msg = e.response?.data?.error || 'Erro ao excluir turma.'
    await proxy.$alert(msg, 'Erro')
  }
}
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
</style>